import type {
  OverageSettingsResponse,
  SpendingControls,
} from "@/types_api/backend/billing/overage_settings_response"
import type { OverageSettingsUpdate } from "@/types_api/backend/billing/overage_settings_update"

export interface OverageSettingsDraft {
  overagesEnabled: boolean
  limitState: OverageSettingsUpdate["limit_state"]
  amount: string
}

export function currencyFractionDigits(currency: string): number | null {
  if (!Intl.supportedValuesOf("currency").includes(currency)) {
    return null
  }
  return (
    new Intl.NumberFormat("en", {
      style: "currency",
      currency,
    }).resolvedOptions().maximumFractionDigits ?? null
  )
}

export function minorAmountInput(amount: number, currency: string): string {
  const digits = currencyFractionDigits(currency) ?? 0
  const integer = BigInt(amount)
    .toString()
    .padStart(digits + 1, "0")
  return digits === 0
    ? integer
    : `${integer.slice(0, -digits)}.${integer.slice(-digits)}`
}

export function createOverageSettingsDraft(
  controls: SpendingControls,
): OverageSettingsDraft {
  return {
    overagesEnabled: controls.overages_enabled,
    limitState: controls.limit_state,
    amount:
      controls.limit_minor === null
        ? ""
        : minorAmountInput(controls.limit_minor, controls.currency),
  }
}

export function validateOverageSettingsDraft(
  draft: OverageSettingsDraft,
  currency: string,
): { limitMinor: number | null; amountError: string | null } {
  if (draft.limitState !== "limited") {
    return { limitMinor: null, amountError: null }
  }
  const digits = currencyFractionDigits(currency) ?? 0
  const pattern =
    digits === 0 ? /^\d+$/ : new RegExp(`^\\d+(?:\\.\\d{1,${digits}})?$`)
  if (draft.amount.length > 40 || !pattern.test(draft.amount)) {
    return {
      limitMinor: null,
      amountError:
        digits === 0
          ? "Enter a nonnegative whole amount."
          : `Enter a nonnegative amount with at most ${digits} decimal places.`,
    }
  }
  const [whole, fraction = ""] = draft.amount.split(".")
  const minor = BigInt(`${whole}${fraction.padEnd(digits, "0")}`)
  if (minor > BigInt(Number.MAX_SAFE_INTEGER)) {
    return {
      limitMinor: null,
      amountError:
        "Enter an amount no greater than 9,007,199,254,740,991 minor units.",
    }
  }
  return { limitMinor: Number(minor), amountError: null }
}

export function assertOverageSettingsScope(
  response: OverageSettingsResponse,
  organizationId: string,
): void {
  const controls = response.controls
  if (
    response.organization_id !== organizationId ||
    response.configured !== (controls !== null) ||
    (controls !== null &&
      (controls.organization_id !== organizationId ||
        !/^[A-Z]{3}$/.test(controls.currency) ||
        (controls.limit_state === "limited") !==
          (controls.limit_minor !== null) ||
        (controls.limit_minor !== null &&
          (!Number.isSafeInteger(controls.limit_minor) ||
            controls.limit_minor < 0))))
  ) {
    throw new Error("Settings could not be verified for this organization.")
  }
}
